import React, { useState, useEffect } from 'react';
// Force refresh: v2
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { useOrganization } from '@clerk/clerk-react';

interface FloorPlanSharingModalProps {
  open: boolean;
  onClose: () => void;
  floorPlanId: string;
  userEmail: string;
  appUrl: string;
}

export function FloorPlanSharingModal({
  open,
  onClose,
  floorPlanId,
  userEmail,
  appUrl,
}: FloorPlanSharingModalProps) {
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [linkStatus, setLinkStatus] = useState<'none' | 'active' | 'revoked'>('none');

  const { organization } = useOrganization();

  // Load existing public link when the modal is opened
  useEffect(() => {
    if (!open) return;

    const fetchExistingLink = async () => {
      try {
        const supabase = await getAuthenticatedSupabaseClient();
        const { data, error } = await supabase
          .from('floor_plans')
          .select('public_link_token, public_link_expires_at')
          .eq('id', floorPlanId)
          .single();

        if (error) throw error;

        const fpData = data as any;
        if (fpData?.public_link_token) {
          // Build the public URL using the existing token
          // Ensure appUrl is valid
          let validAppUrl = appUrl;
          if (!validAppUrl || validAppUrl.includes('undefined')) {
            validAppUrl = window.location.origin;
          }

          // Handle localhost URLs for development
          if (validAppUrl.includes('localhost') || validAppUrl.includes('127.0.0.1')) {
            // For development, use the URL as-is without validation
            validAppUrl = validAppUrl.replace(/\/$/, '');
          }

          const url = `${validAppUrl}/shared/floor-plans/${fpData.public_link_token}`;
          setPublicUrl(url);
          setLinkStatus('active');

          // Pre-populate the expiration input so users can see/edit it
          if (fpData.public_link_expires_at) {
            // Convert to date format (YYYY-MM-DD)
            const date = new Date(fpData.public_link_expires_at);
            // Format as YYYY-MM-DD for the date input
            const dateString = date.toISOString().split('T')[0];
            setExpiresAt(dateString);
          }
        } else {
          // Reset state if no link exists
          setPublicUrl(null);
          setLinkStatus('none');
          setExpiresAt('');
        }
      } catch (err) {
        logger.error('Failed to fetch existing share link', err);
      }
    };

    fetchExistingLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, floorPlanId]);

  // Convert date-only string to end of day (23:59:59) datetime
  const getEndOfDayTimestamp = (dateStr: string): string => {
    // If the string is empty, return empty
    if (!dateStr) return '';
    // Create date object from the input date
    const date = new Date(dateStr);
    // Set time to 23:59:59
    date.setHours(23, 59, 59, 999);
    // Return ISO string
    return date.toISOString();
  };

  // Add a handler for the date change
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateOnlyValue = e.target.value; // This is YYYY-MM-DD format
    setExpiresAt(dateOnlyValue);
  };

  const handleGenerateLink = async () => {
    if (!expiresAt) {
      toast.error('Please set an expiration date.');
      return;
    }

    // Convert the date-only value to end of day timestamp
    const expiryTimestamp = getEndOfDayTimestamp(expiresAt);

    setLoading(true);

    try {
      // First, check if this floor plan has workspace support through its project
      const supabase = await getAuthenticatedSupabaseClient();
      const { data: floorPlanData, error: fpCheckError } = await supabase
        .from('floor_plans')
        .select('project_id, created_by, projects!inner(workspace_id)')
        .eq('id', floorPlanId)
        .single();

      if (fpCheckError) {
        throw new Error('Failed to check floor plan permissions');
      }

      logger.log('Floor plan data:', floorPlanData);

      if (!floorPlanData.projects?.workspace_id) {
        // This is a legacy floor plan without workspace support
        logger.warn('This floor plan does not have workspace support. Sharing may fail.');
      }
      // Prepare sender email
      let sender = userEmail;
      if (!sender) {
        sender = userEmail || 'noreply@vfloow.com';
      }

      // Ensure appUrl is valid and properly formatted
      let validAppUrl = appUrl;
      if (!validAppUrl || validAppUrl.includes('undefined')) {
        validAppUrl = window.location.origin;
      }
      validAppUrl = validAppUrl.replace(/\/$/, '');

      // Use supabase.functions.invoke() for automatic JWT handling
      const { data, error } = await supabase.functions.invoke('share-floor-plan-public', {
        body: {
          floorPlanId,
          expiresAt: expiryTimestamp,
          recipientEmail: recipientEmail || undefined,
          senderEmail: sender,
          appUrl: validAppUrl,
          clerkOrgId: organization?.id,
        },
      });

      if (error) {
        // Handle specific error types
        if (error.message?.includes('403') || error.message?.includes('Permission')) {
          throw new Error(
            'Permission denied. You need editor access or higher to share this floor plan.',
          );
        } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (error.message?.includes('404')) {
          throw new Error('Floor plan not found.');
        } else {
          throw new Error(error.message || 'Failed to generate link');
        }
      }
      setPublicUrl(data.publicUrl);
      setLinkStatus('active');
      toast.success('Public link generated!');
    } catch (err: any) {
      logger.error('Share floor plan error:', err);
      toast.error(err.message || 'Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (publicUrl) {
      navigator.clipboard.writeText(publicUrl);
      toast.success('Link copied to clipboard');
    }
  };

  const handleRevoke = async () => {
    setLoading(true);
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      // Use supabase.functions.invoke() for automatic JWT handling
      const { data, error } = await supabase.functions.invoke('share-floor-plan-revoke', {
        body: {
          floorPlanId,
          clerkOrgId: organization?.id,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to revoke public link');
      }

      setPublicUrl(null);
      setLinkStatus('revoked');
      toast.success('Public link revoked');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share Floor Plan</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="expiresAt">Link Expiration Date</Label>
            <Input
              id="expiresAt"
              type="date"
              value={expiresAt}
              onChange={handleDateChange}
              required
            />
            {expiresAt && (
              <p className="text-xs text-muted-foreground mt-1">
                Link will expire at 11:59 PM on the selected date
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="recipientEmail">Send to Email (optional)</Label>
            <Input
              id="recipientEmail"
              type="email"
              value={recipientEmail}
              onChange={e => setRecipientEmail(e.target.value)}
              placeholder="customer@email.com"
            />
          </div>
          {publicUrl && linkStatus === 'active' && (
            <div className="space-y-2">
              <Label>Public Link</Label>
              <div className="flex items-center gap-2">
                <Input value={publicUrl} readOnly className="flex-1" />
                <Button type="button" variant="outline" onClick={handleCopy}>
                  Copy
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">
                Expires at:{' '}
                {expiresAt
                  ? new Date(getEndOfDayTimestamp(expiresAt)).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    }) + ' at 11:59 PM'
                  : ''}
              </div>
            </div>
          )}
          {linkStatus === 'revoked' && (
            <div className="text-destructive text-sm">Link revoked.</div>
          )}
        </div>
        <DialogFooter>
          {publicUrl && linkStatus === 'active' ? (
            <Button type="button" variant="destructive" onClick={handleRevoke} disabled={loading}>
              Revoke Link
            </Button>
          ) : (
            <Button type="button" onClick={handleGenerateLink} disabled={loading}>
              Generate Link
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
